import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useMe } from '../../hooks/useMe'
import { clearToken, getToken } from '../../lib/auth'
import NavBar from '../../components/layout/NavBar'

export function shouldUseMobileSettingsLayout(viewportWidth: number): boolean {
  return viewportWidth < 1024
}

export function isSettingsRootPath(pathname: string): boolean {
  return pathname === '/settings' || pathname === '/settings/'
}

function NavItem({
  to,
  children,
  comingSoon,
  activeOverride,
}: {
  to: string
  children: React.ReactNode
  comingSoon?: boolean
  activeOverride?: boolean
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block min-h-11 px-3 py-2.5 rounded-lg ${activeOverride ?? isActive ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'}`
      }
      end
    >
      <div className="flex items-center justify-between gap-2">
        <span>{children}</span>
        {comingSoon ? (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/55">
            Coming soon
          </span>
        ) : null}
      </div>
    </NavLink>
  )
}

export default function SettingsLayout() {
  const location = useLocation()
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : shouldUseMobileSettingsLayout(window.innerWidth)
  )
  const isRoot = Boolean(me?.is_superuser)
  const isStaff = Boolean(me?.is_staff)
  const ownerFeatureEnabled = true
  const isOwner = isRoot && ownerFeatureEnabled
  const isSettingsRoot = isSettingsRootPath(location.pathname)
  const showMobileList = isNarrow && isSettingsRoot
  const showMobileDetail = isNarrow && !isSettingsRoot

  useEffect(() => {
    // Ensure each settings route starts at the top.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    if (document?.documentElement) document.documentElement.scrollTop = 0
    if (document?.body) document.body.scrollTop = 0
  }, [location.pathname, location.search])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onResize = () => setIsNarrow(shouldUseMobileSettingsLayout(window.innerWidth))
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const nav = (
    <>
      <NavItem to="/settings/profile" activeOverride={isSettingsRoot}>
        Profile
      </NavItem>
      <NavItem to="/settings/pixe-photos">Pixe Photos</NavItem>
      <NavItem to="/settings/handles">Handles</NavItem>
      <NavItem to="/settings/account">Account</NavItem>
      <NavItem to="/settings/security">Security</NavItem>
      {isRoot && (
        <NavItem to="/settings/moderation">Moderation</NavItem>
      )}
      {isStaff && <NavItem to="/admin/disputes">Admin Disputes</NavItem>}
      <NavItem to="/settings/notifications">Notifications</NavItem>
      <NavItem to="/settings/privacy">Privacy</NavItem>
      <NavItem to="/settings/wallet">Wallet</NavItem>
      <NavItem to="/settings/subscriptions">My Subscriptions</NavItem>
      <NavItem to="/settings/billing">Billing</NavItem>
      {isRoot && <NavItem to="/settings/treasury">Treasury</NavItem>}
      {isRoot && <NavItem to="/settings/telemetry">Telemetry Logs</NavItem>}
      {isOwner && <NavItem to="/settings/owner">Owner Console</NavItem>}
      <div className="mt-3 mb-1 text-xs uppercase tracking-wide text-white/50">Creator Tools</div>
      <NavItem to="/settings/pixe-publish">Pixe Publish</NavItem>
      <NavItem to="/pros">
        Pixe Pros
      </NavItem>
      <NavItem to="/jobs">Jobs</NavItem>
      <NavItem to="/pros/requests">Pros Requests</NavItem>
      <NavItem to="/settings/pages">Pixe Pages</NavItem>
      <NavItem to="/settings/companies">Companies</NavItem>
      <NavItem to="/settings/creator/subscriptions">
        Pixe Subscriptions
      </NavItem>
      <NavItem to="/settings/creator-tools/platform-fee">Platform Fee</NavItem>
      <NavItem to="/settings/creator-tools/events">Pixe Events</NavItem>
      <NavItem to="/settings/payouts">Payouts</NavItem>
      <div className="mt-3 mb-1 text-xs uppercase tracking-wide text-white/50">Earnings</div>
      <NavItem to="/settings/earnings/ambassador">Ambassador</NavItem>
      <div className="mt-3 mb-1 text-xs uppercase tracking-wide text-white/50">Legal & Support</div>
      <NavItem to="/settings/feedback">Support</NavItem>
      <NavItem to="/settings/legal">Legal</NavItem>
    </>
  )

  function onMobileLogout() {
    clearToken()
    window.location.assign('/login')
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} showMobileModePill={false} />

      {isNarrow ? (
        <main className="mx-auto max-w-7xl px-4 py-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {showMobileList ? (
            <section className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <div className="mb-2 px-1 text-lg font-semibold text-white">Settings</div>
              <nav className="space-y-1">{nav}</nav>
              {authed ? (
                <div className="mt-4 border-t border-white/10 pt-3">
                  <button
                    type="button"
                    onClick={onMobileLogout}
                    className="block min-h-11 w-full rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-left text-red-200 hover:bg-red-500/20"
                  >
                    Log out
                  </button>
                </div>
              ) : null}
            </section>
          ) : (
            <section className="space-y-3">
              <Link
                to="/settings"
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10"
              >
                <span aria-hidden="true">←</span>
                <span>Back to settings</span>
              </Link>
              {showMobileDetail ? <Outlet /> : null}
            </section>
          )}
        </main>
      ) : (
        <main className="mx-auto max-w-7xl px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-3">
            <nav className="space-y-1">{nav}</nav>
          </aside>
          <section className="lg:col-span-9">
            <Outlet />
          </section>
        </main>
      )}
    </div>
  )
}
